import React, { useState, useEffect } from 'react';
import ContactModal from '../showcase/ContactModal';

const Landing: React.FC = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isStrategicPlanningModalOpen, setIsStrategicPlanningModalOpen] = useState(false);
  const [isCompetitiveAnalysisModalOpen, setIsCompetitiveAnalysisModalOpen] = useState(false);

  const [isCommunityEngagementModalOpen, setIsCommunityEngagementModalOpen] = useState(false);

  const openContactModal = () => setIsContactModalOpen(true);
  const closeContactModal = () => setIsContactModalOpen(false);

  // Warm Grey/Cream theme colors
  const theme = {
    brand: '#6B7280', // Warm grey
    accent: '#9CA3AF', // Lighter grey
    neutral: '#FDF6E3', // Warm cream
    text: '#374151', // Dark grey text
    glow: '#D1D5DB' // Subtle glow
  };

  // Apply theme to CSS variables
  useEffect(() => {
    document.documentElement.style.setProperty('--brand', theme.brand);
    document.documentElement.style.setProperty('--accent', theme.accent);
    document.documentElement.style.setProperty('--neutral', theme.neutral);
    document.documentElement.style.setProperty('--text', theme.text);
    document.documentElement.style.setProperty('--glow', theme.glow);
  }, [theme.brand, theme.accent, theme.neutral, theme.text, theme.glow]);

  const glassCardStyle = {
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
  };

  const btnPrimaryStyle = {
    background: 'var(--brand)',
    color: 'var(--neutral)',
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
  };



  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--neutral)' }}>
      {/* Background gradient blobs for glassmorphism effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 rounded-full opacity-10" style={{ background: 'var(--brand)', filter: 'blur(80px)' }}></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full opacity-15" style={{ background: 'var(--accent)', filter: 'blur(60px)' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full opacity-10" style={{ background: 'var(--brand)', filter: 'blur(100px)' }}></div>
      </div>
      
      {/* Main content with glassmorphism */}
      <div className="relative z-10 min-h-screen backdrop-blur-sm">
             {/* Header */}
       <header className="py-6 px-4">
         <div className="container mx-auto flex justify-center items-center">
           <div className="relative">
                                       <img 
               src="/ikon.bulbflash.png" 
               alt="IKON Consulting" 
               className="h-80 w-auto logo-flash"
               style={{ maxWidth: 'none' }}
             />

           </div>
         </div>
       </header>

                                                                                                                               {/* Divider */}
           <div className="flex justify-center py-8">
             <div className="w-full max-w-4xl h-1" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)' }}></div>
           </div>

           {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="p-12 rounded-3xl backdrop-blur-md" style={glassCardStyle}>
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                Strategic Consulting for
                <span className="block" style={{ color: 'var(--brand)' }}>Nonprofit Excellence</span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto" style={{ color: 'var(--text)' }}>
                IKON Consulting is a strategic advisory firm dedicated to helping businesses and non-profits 
                define their purpose, align around a clear mission, and achieve long-term goals with confidence and clarity.
              </p>
              <button 
                className="text-lg px-8 py-4 rounded-lg font-semibold transition-all duration-300 hover:transform hover:-translate-y-1"
                style={btnPrimaryStyle}
                onClick={openContactModal}
              >
                Start Your Strategy
              </button>
            </div>
          </div>
        </div>
      </section>

                                                                                                                                                                                                                                                                                                                                                                                                                                                               {/* Divider */}
            <div className="flex justify-center py-8">
              <div className="w-full max-w-4xl h-1" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)' }}></div>
            </div>

                  {/* Services Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="p-12 rounded-3xl backdrop-blur-md mb-12" style={glassCardStyle}>
            <h2 className="text-4xl font-bold text-center" style={{ color: 'var(--text)' }}>
              Our Strategic Services
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Top Row - 3 Services */}
            <div className="p-8 cursor-pointer" style={glassCardStyle} onClick={() => setIsCompetitiveAnalysisModalOpen(true)}>
              <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center" style={{ background: 'var(--brand)' }}>
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4" style={{ color: 'var(--text)' }}>
                Competitive Analysis
              </h3>
              <p className="mb-4" style={{ color: 'var(--text)' }}>
                Comprehensive market analysis to understand your competitive landscape and identify 
                strategic opportunities for differentiation.
              </p>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text)' }}>
                <li>• SWOT and Porter's Five Forces</li>
                <li>• Market share and positioning maps</li>
                <li>• Competitor benchmarking dashboards</li>
              </ul>
            </div>

            <div className="p-8 cursor-pointer" style={glassCardStyle} onClick={() => setIsStrategicPlanningModalOpen(true)}>
              <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center" style={{ background: 'var(--accent)' }}>
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4" style={{ color: 'var(--text)' }}>
                Strategic Planning & Vision
              </h3>
              <p className="mb-4" style={{ color: 'var(--text)' }}>
                Comprehensive strategic planning for C-suite executives including mission/vision alignment, 
                long-term goal setting, and actionable roadmaps for sustainable growth.
              </p>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text)' }}>
                <li>• Annual strategic plans & executive offsites</li>
                <li>• Mission/vision workshops & alignment</li>
                <li>• Long-term goal frameworks (SMART, OKRs)</li>
              </ul>
            </div>

            <div className="p-8 cursor-pointer" style={glassCardStyle} onClick={() => setIsCommunityEngagementModalOpen(true)}>
              <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center" style={{ background: 'var(--brand)' }}>
                <span className="text-2xl">🤝</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4" style={{ color: 'var(--text)' }}>
                Community Engagement
              </h3>
              <p className="mb-4" style={{ color: 'var(--text)' }}>
                Build meaningful connections with your community through strategic engagement 
                initiatives that drive impact and foster lasting relationships.
              </p>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text)' }}>
                <li>• Stakeholder mapping and engagement</li>
                <li>• Community partnership development</li>
                <li>• Public relations and communication</li>
              </ul>
            </div>

            {/* Bottom Row - 2 Centered Services */}
            <div className="lg:col-span-3 flex justify-center space-x-8">
              <div className="p-8 cursor-pointer" style={glassCardStyle} onClick={() => setIsAiModalOpen(true)}>
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center" style={{ background: 'var(--accent)' }}>
                  <span className="text-2xl">🤖</span>
                </div>
                <h3 className="text-2xl font-semibold mb-4" style={{ color: 'var(--text)' }}>
                  AI Business Strategy
                </h3>
                <p className="mb-4" style={{ color: 'var(--text)' }}>
                  Leverage artificial intelligence to transform your business operations and 
                  create competitive advantages in the digital age.
                </p>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--text)' }}>
                  <li>• AI implementation roadmaps</li>
                  <li>• Digital transformation strategies</li>
                  <li>• Technology adoption frameworks</li>
                </ul>
              </div>

              <div className="p-8 cursor-pointer" style={glassCardStyle} onClick={() => setIsProjectModalOpen(true)}>
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center" style={{ background: 'var(--brand)' }}>
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-2xl font-semibold mb-4" style={{ color: 'var(--text)' }}>
                  Project/Program Management
                </h3>
                <p className="mb-4" style={{ color: 'var(--text)' }}>
                  End-to-end project and program management services to ensure successful 
                  implementation and delivery of strategic initiatives.
                </p>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--text)' }}>
                  <li>• RFP/RFQ development</li>
                  <li>• Project launch and implementation</li>
                  <li>• Program management and oversight</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

                                                                                                                                                                                                                                                                                                                                                                                                                                                               {/* Divider */}
            <div className="flex justify-center py-8">
              <div className="w-full max-w-4xl h-1" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)' }}></div>
            </div>

                  {/* Meet Our Founder Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="p-12 rounded-3xl backdrop-blur-md" style={glassCardStyle}>
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold" style={{ color: 'var(--text)' }}>
                  Meet Our Founder
                </h2>
              </div>
              <div className="flex flex-col md:flex-row items-center gap-8">
                <img
                  src="/lashun%20headshot.jpg"
                  alt="Ms. Ikoie Norman - Founder of IKON Consulting"
                  className="w-64 h-64 object-cover rounded-full shadow-2xl"
                />
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--brand)' }}>
                    Ms. Ikoie Norman
                  </h3>
                  <p className="text-lg mb-4" style={{ color: 'var(--text)' }}>
                    With over 15 years of experience in strategic consulting and nonprofit development, 
                    Ms. Norman has helped organizations across the country achieve their mission-driven goals.
                  </p>
                  <p className="text-lg" style={{ color: 'var(--text)' }}>
                    Her expertise spans strategic planning, organizational development, and community engagement, 
                    making her a trusted advisor for nonprofits and social impact organizations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

                                                                                                                                                                                                                                                                                                                                                                                                                                                               {/* Divider */}
            <div className="flex justify-center py-8">
              <div className="w-full max-w-4xl h-1" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)' }}></div>
            </div>

                  {/* Testimonials Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto">
            <div className="p-12 rounded-3xl backdrop-blur-md text-center" style={glassCardStyle}>
              <blockquote className="text-xl mb-6 italic" style={{ color: 'var(--text)' }}>
                "IKON Consulting transformed our approach to strategic planning. Their expertise in 
                nonprofit development helped us increase our impact by 40% while building sustainable 
                partnerships that will serve our community for years to come."
              </blockquote>
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-gray-300 mr-4"></div>
                <div>
                  <p className="font-semibold" style={{ color: 'var(--text)' }}>Sarah Johnson</p>
                  <p className="text-sm" style={{ color: 'var(--text)' }}>Executive Director, Community Foundation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

                                                                                                                                                                                                                                                                                                                                                                                                                                                               {/* Divider */}
            <div className="flex justify-center py-8">
              <div className="w-full max-w-4xl h-1" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)' }}></div>
            </div>

            {/* Contact Section */}
       <section id="contact" className="py-16 px-4">
        <div className="container mx-auto">
          <div className="p-12 max-w-2xl mx-auto text-center" style={glassCardStyle}>
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--text)' }}>
              Ready to Transform Your Impact?
            </h2>
            <p className="text-lg mb-8" style={{ color: 'var(--text)' }}>
              Let's discuss how strategic consulting can elevate your organization's mission and create lasting change.
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-center space-x-4">
                <span className="text-lg">📧</span>
                <span style={{ color: 'var(--text)' }}>info@ikonconsultinggroup.com</span>
              </div>
              <div className="flex items-center justify-center space-x-4">
                <span className="text-lg">📞</span>
                <span style={{ color: 'var(--text)' }}>678-756-9933</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="hover:transform hover:-translate-y-0.5 hover:shadow-lg"
                style={btnPrimaryStyle}
                onClick={openContactModal}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px var(--glow)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.1)';
                }}
              >
                Schedule Consultation
              </button>

            </div>
          </div>
        </div>
      </section>

             {/* Footer */}
       <footer className="py-8 px-4">
         <div className="container mx-auto">
           <div className="max-w-2xl mx-auto">
             <div className="p-6 rounded-2xl backdrop-blur-md text-center" style={glassCardStyle}>
               <p className="text-sm" style={{ color: 'var(--text)' }}>
                 © 2025 IKON Consulting. Empowering businesses through strategic excellence.
               </p>
             </div>
           </div>
         </div>
       </footer>
      </div>

      {/* AI Business Strategy Modal */}
      {isAiModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8 modal-scroll" style={{ 
            background: 'var(--neutral)', 
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <div>
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent)' }}>
                    <span className="text-3xl">🤖</span>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold" style={{ color: 'var(--brand)' }}>AI Business Strategy</h2>
                    <p className="text-lg" style={{ color: 'var(--text)' }}>Digital Transformation & Innovation</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsAiModalOpen(false)}
                  className="text-2xl hover:opacity-70 transition-opacity"
                  style={{ color: 'var(--text)' }}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Overview</h3>
                  <p className="text-lg leading-relaxed" style={{ color: 'var(--text)' }}>
                    Transform your organization with cutting-edge AI strategies that drive innovation, 
                    efficiency, and competitive advantage. Our AI Business Strategy service helps you 
                    navigate the complex landscape of artificial intelligence implementation.
                  </p>
                </div>

                {/* Divider */}
                <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)', opacity: 0.3 }}></div>

                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Key Services</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>AI Implementation Roadmaps</h4>
                      <p className="text-sm" style={{ color: 'var(--text)' }}>
                        Comprehensive roadmaps for AI adoption, including technology selection, 
                        implementation timelines, and ROI projections.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Digital Transformation Strategies</h4>
                      <p className="text-sm" style={{ color: 'var(--text)' }}>
                        End-to-end digital transformation planning with AI as a core component 
                        of your organizational evolution.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Technology Adoption Frameworks</h4>
                      <p className="text-sm" style={{ color: 'var(--text)' }}>
                        Proven frameworks for successful AI technology adoption, including 
                        change management and training strategies.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>AI Ethics & Governance</h4>
                      <p className="text-sm" style={{ color: 'var(--text)' }}>
                        Development of AI ethics policies, governance frameworks, and 
                        responsible AI implementation guidelines.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)', opacity: 0.3 }}></div>

                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Expected Outcomes</h3>
                  <ul className="space-y-2" style={{ color: 'var(--text)' }}>
                    <li>• 30-50% increase in operational efficiency through AI automation</li>
                    <li>• Enhanced decision-making capabilities with AI-powered insights</li>
                    <li>• Competitive advantage through innovative AI applications</li>
                    <li>• Scalable AI infrastructure for future growth</li>
                    <li>• Improved customer experience through AI-enhanced services</li>
                  </ul>
                </div>

                <div className="flex justify-center pt-6">
                  <button 
                    onClick={openContactModal}
                    className="px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:transform hover:-translate-y-1"
                    style={{ background: 'var(--brand)', color: 'var(--text)' }}
                  >
                    Get Started with AI Strategy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project/Program Management Modal */}
      {isProjectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8 modal-scroll" style={{ 
            background: 'var(--neutral)', 
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <div>
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--brand)' }}>
                    <span className="text-3xl">📋</span>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold" style={{ color: 'var(--brand)' }}>Project/Program Management</h2>
                    <p className="text-lg" style={{ color: 'var(--text)' }}>Implementation & Delivery Excellence</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsProjectModalOpen(false)}
                  className="text-2xl hover:opacity-70 transition-opacity"
                  style={{ color: 'var(--text)' }}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Overview</h3>
                  <p className="text-lg leading-relaxed" style={{ color: 'var(--text)' }}>
                    Ensure successful delivery of your strategic initiatives with our comprehensive 
                    project and program management services. From initial planning to final implementation, 
                    we provide the expertise and oversight needed to achieve your organizational goals.
                  </p>
                </div>

                {/* Divider */}
                <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)', opacity: 0.3 }}></div>

                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Key Services</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>RFP/RFQ Development</h4>
                      <p className="text-sm" style={{ color: 'var(--text)' }}>
                        Professional development of Requests for Proposals and Quotes, 
                        ensuring clear requirements and optimal vendor selection.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Project Launch & Implementation</h4>
                      <p className="text-sm" style={{ color: 'var(--text)' }}>
                        Comprehensive project launch services including stakeholder alignment, 
                        timeline development, and milestone tracking.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Program Management & Oversight</h4>
                      <p className="text-sm" style={{ color: 'var(--text)' }}>
                        Ongoing program management with regular reporting, risk mitigation, 
                        and performance optimization.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Change Management</h4>
                      <p className="text-sm" style={{ color: 'var(--text)' }}>
                        Strategic change management support to ensure smooth transitions 
                        and stakeholder buy-in throughout project implementation.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)', opacity: 0.3 }}></div>

                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Expected Outcomes</h3>
                  <ul className="space-y-2" style={{ color: 'var(--text)' }}>
                    <li>• 95%+ project completion rate on time and within budget</li>
                    <li>• Improved stakeholder satisfaction and communication</li>
                    <li>• Reduced project risks and enhanced mitigation strategies</li>
                    <li>• Streamlined processes and increased efficiency</li>
                    <li>• Sustainable project management practices for future initiatives</li>
                  </ul>
                </div>

                <div className="flex justify-center pt-6">
                  <button 
                    onClick={openContactModal}
                    className="px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:transform hover:-translate-y-1"
                    style={{ background: 'var(--brand)', color: 'var(--text)' }}
                  >
                    Start Your Project
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Strategic Planning & Vision Modal */}
      {isStrategicPlanningModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8 rounded-2xl modal-scroll" style={{ 
            background: 'var(--neutral)', 
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent)' }}>
                  <span className="text-3xl">🎯</span>
                </div>
                <div>
                  <h2 className="text-3xl font-bold" style={{ color: 'var(--brand)' }}>Strategic Planning & Vision</h2>
                  <p className="text-lg" style={{ color: 'var(--text)' }}>C-Suite Executive Strategic Planning</p>
                </div>
              </div>
              <button 
                onClick={() => setIsStrategicPlanningModalOpen(false)}
                className="text-2xl hover:opacity-70 transition-opacity"
                style={{ color: 'var(--text)' }}
              >
                ×
              </button>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Overview</h3>
                <p className="text-lg leading-relaxed" style={{ color: 'var(--text)' }}>
                  Transform your organization's future with comprehensive strategic planning designed specifically 
                  for C-suite executives. Our proven methodology aligns leadership teams around clear vision, 
                  actionable goals, and sustainable growth strategies that drive measurable results.
                </p>
              </div>

              {/* Divider */}
              <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)', opacity: 0.3 }}></div>

              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Core Services</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Annual Strategic Plans & Executive Offsites</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Facilitated executive retreats and strategic planning sessions that bring together 
                      leadership teams to develop comprehensive annual strategies with clear priorities, 
                      resource allocation, and success metrics.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Mission/Vision Workshops & Alignment</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Collaborative workshops to refine or develop your organization's mission and vision 
                      statements, ensuring alignment across all departments and stakeholder groups.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Long-term Goal Frameworks (SMART, OKRs)</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Implementation of proven goal-setting frameworks including SMART goals and 
                      Objectives and Key Results (OKRs) to ensure measurable progress and accountability.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Strategic Roadmap Development</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Detailed implementation roadmaps with timelines, milestones, and resource requirements 
                      to guide your organization's strategic journey.
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)', opacity: 0.3 }}></div>

              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Expected Outcomes</h3>
                <ul className="space-y-2" style={{ color: 'var(--text)' }}>
                  <li>• Clear, actionable strategic plan with measurable objectives</li>
                  <li>• Aligned leadership team with shared vision and priorities</li>
                  <li>• Improved decision-making framework and resource allocation</li>
                  <li>• Enhanced organizational focus and execution capabilities</li>
                  <li>• Sustainable competitive advantage and market positioning</li>
                </ul>
              </div>

              <div className="flex justify-center pt-6">
                <button 
                  onClick={openContactModal}
                  className="px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:transform hover:-translate-y-1"
                  style={{ background: 'var(--brand)', color: 'var(--text)' }}
                >
                  Start Your Strategic Planning
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Competitive Analysis Modal */}
      {isCompetitiveAnalysisModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8 rounded-2xl modal-scroll" style={{ 
            background: 'var(--neutral)', 
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--brand)' }}>
                  <span className="text-3xl">📊</span>
                </div>
                <div>
                  <h2 className="text-3xl font-bold" style={{ color: 'var(--brand)' }}>Competitive Analysis</h2>
                  <p className="text-lg" style={{ color: 'var(--text)' }}>Market Intelligence & Strategic Positioning</p>
                </div>
              </div>
              <button 
                onClick={() => setIsCompetitiveAnalysisModalOpen(false)}
                className="text-2xl hover:opacity-70 transition-opacity"
                style={{ color: 'var(--text)' }}
              >
                ×
              </button>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Overview</h3>
                <p className="text-lg leading-relaxed" style={{ color: 'var(--text)' }}>
                  Gain a comprehensive understanding of your competitive landscape through data-driven analysis 
                  and strategic insights. Our competitive analysis services provide the intelligence you need 
                  to identify opportunities, mitigate threats, and position your organization for success.
                </p>
              </div>

              {/* Divider */}
              <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)', opacity: 0.3 }}></div>

              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Core Services</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>SWOT & Porter's Five Forces Analysis</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Comprehensive Strengths, Weaknesses, Opportunities, and Threats analysis combined 
                      with Porter's Five Forces framework to assess competitive intensity and market dynamics.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Market Share & Positioning Maps</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Visual mapping of market share distribution and competitive positioning to identify 
                      gaps, opportunities, and strategic advantages in your market space.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Competitor Benchmarking Dashboards</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Interactive dashboards comparing key performance indicators, capabilities, and 
                      strategies across your competitive landscape for informed decision-making.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Market Intelligence Reports</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Detailed reports on market trends, emerging competitors, and strategic implications 
                      to keep your organization ahead of market changes.
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)', opacity: 0.3 }}></div>

              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Expected Outcomes</h3>
                <ul className="space-y-2" style={{ color: 'var(--text)' }}>
                  <li>• Clear understanding of competitive landscape and market dynamics</li>
                  <li>• Identified strategic opportunities and potential threats</li>
                  <li>• Data-driven positioning strategies and differentiation opportunities</li>
                  <li>• Enhanced market intelligence and competitive awareness</li>
                  <li>• Informed strategic decision-making and resource allocation</li>
                </ul>
              </div>

              <div className="flex justify-center pt-6">
                <button 
                  onClick={openContactModal}
                  className="px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:transform hover:-translate-y-1"
                  style={{ background: 'var(--brand)', color: 'var(--text)' }}
                >
                  Start Your Competitive Analysis
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Community Engagement Modal */}
      {isCommunityEngagementModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8 rounded-2xl modal-scroll" style={{ 
            background: 'var(--neutral)', 
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--brand)' }}>
                  <span className="text-3xl">🤝</span>
                </div>
                <div>
                  <h2 className="text-3xl font-bold" style={{ color: 'var(--brand)' }}>Community Engagement</h2>
                  <p className="text-lg" style={{ color: 'var(--text)' }}>Strategic Partnership & Stakeholder Development</p>
                </div>
              </div>
              <button 
                onClick={() => setIsCommunityEngagementModalOpen(false)}
                className="text-2xl hover:opacity-70 transition-opacity"
                style={{ color: 'var(--text)' }}
              >
                ×
              </button>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Overview</h3>
                <p className="text-lg leading-relaxed" style={{ color: 'var(--text)' }}>
                  Build meaningful, lasting relationships with your community through strategic engagement 
                  initiatives that drive impact and foster collaboration. Our community engagement services 
                  help organizations connect authentically with stakeholders and create sustainable partnerships.
                </p>
              </div>

              {/* Divider */}
              <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)', opacity: 0.3 }}></div>

              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Core Services</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Stakeholder Mapping & Engagement</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Comprehensive stakeholder identification and mapping to understand influence, 
                      interests, and engagement strategies for key community groups and individuals.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Community Partnership Development</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Strategic development of community partnerships and collaborative initiatives 
                      that create mutual value and strengthen organizational impact.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Public Relations & Communication</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Strategic communication planning and public relations support to enhance 
                      community visibility and build positive relationships with key stakeholders.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Community Impact Assessment</h4>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>
                      Measurement and evaluation of community engagement initiatives to demonstrate 
                      impact and inform future strategic decisions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="w-full h-px" style={{ background: 'linear-gradient(to right, transparent, var(--brand), transparent)', opacity: 0.3 }}></div>

              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--brand)' }}>Expected Outcomes</h3>
                <ul className="space-y-2" style={{ color: 'var(--text)' }}>
                  <li>• Stronger community relationships and stakeholder trust</li>
                  <li>• Enhanced organizational reputation and community visibility</li>
                  <li>• Increased community support and partnership opportunities</li>
                  <li>• Improved communication and engagement strategies</li>
                  <li>• Measurable community impact and sustainable relationships</li>
                </ul>
              </div>

              <div className="flex justify-center pt-6">
                <button 
                  onClick={openContactModal}
                  className="px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:transform hover:-translate-y-1"
                  style={{ background: 'var(--brand)', color: 'var(--text)' }}
                >
                  Start Your Community Engagement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        currentTheme={{
          brand: 'var(--brand)',
          text: 'var(--text)',
          glow: 'var(--glow)'
        }}
      />

             
       {/* CSS for animated logo flashing and custom scrollbar */}
       <style>{`
         .logo-flash {
           animation: logoGlow 3s ease-in-out infinite;
         }
         
         @keyframes logoGlow {
           0%, 100% { 
             filter: brightness(1) drop-shadow(0 0 0px var(--brand));
           }
           50% { 
             filter: brightness(1.3) drop-shadow(0 0 8px var(--brand)) drop-shadow(0 0 15px var(--brand));
           }
         }

         /* Custom scrollbar styling */
         .modal-scroll::-webkit-scrollbar {
           width: 8px;
         }
         
         .modal-scroll::-webkit-scrollbar-track {
           background: var(--neutral);
           border-radius: 4px;
         }
         
         .modal-scroll::-webkit-scrollbar-thumb {
           background: #6b7280;
           border-radius: 4px;
         }
         
         .modal-scroll::-webkit-scrollbar-thumb:hover {
           background: #4b5563;
         }
       `}</style>
     </div>
   );
 };

export default Landing;
